import { Component, HostBinding, ViewChild, ViewEncapsulation, OnInit, ElementRef, ViewChildren, QueryList, ChangeDetectorRef, AfterViewChecked } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { isNullOrUndefined } from '../../../../models/utils';
import { Endpoint, HelpIndex, NotificationSeverityCode, SettingsType } from '../../../../models/enums';
import { EmailServer, EmailServerViewModel } from '../../../../models/email-server';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { BaseModalComponent } from '../../../../models/base-modal-component';
import { DrNamedQuery } from '../../../../models/dr-named-query';
import { KeyValueListComponent } from '../../../key-value-list/key-value-list.component';
import { ScrollablePopoverDirective } from '../../../../directives/scrollable-popover/scrollable-popover.directive';
import { ObjectActionListType, ActionType, Permissions } from '../../../../models/permission';

@Component({
    standalone: false,
    selector: 'res-email-server',
    templateUrl: './email-server.component.html',
    styleUrls: ['./email-server.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class EmailServerComponent extends BaseModalComponent implements SettingsNavigationContentComponent, OnInit, AfterViewChecked {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;
    @ViewChild('queriesKeyValue', { static: true }) queriesKeyValue: KeyValueListComponent;
    @ViewChildren(ScrollablePopoverDirective) errorPopovers: QueryList<ScrollablePopoverDirective>;

    isBusy = false;
    isBusyMessage: string;
    validChanges = true;
    helpIndex: HelpIndex;
    settings: EmailServer;
    viewModel: EmailServerViewModel;
    emailNotificationsAutomationId = 'email-notifications-addresses-';
    notificationAddressList: Array<any>;
    hostId = { errorMessage: 'This field cannot be blank', invalid: false, invalidIP: false };
    hostPort = { errorMessage: 'This field cannot be blank', invalid: false, invalidPort: false };
    mailUserId = { errorMessage: 'This field cannot be blank', invalid: false };
    mailUserPassword = { errorMessage: 'This field cannot be blank', invalid: false };
    isEmailAllDisabled: boolean;
    serverConfig: any;
    hasCreatePermissions: boolean;

    get isDirty(): boolean {
        if (this.contentForm) {
            return this.contentForm.dirty ||
                this.queriesKeyValue?.keyValueForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService, private restService: RestService, private modalService: BsModalService,
        private modalRef: BsModalRef, private changeDetector: ChangeDetectorRef) {
        super(modalService, modalRef);
    }

    ngOnInit() {
        this.viewModel = new EmailServerViewModel(new EmailServer); // to get page to load. Will overwrite when load.
        this.hasCreatePermissions = Permissions.checkUserPermission(ObjectActionListType.EMAIL_SERVER_AND_NOTIFICATIONS, ActionType.CREATE);
        this.loadSettingsData();
    }

    ngAfterViewChecked() {
        this.changeDetector.detectChanges();
    }

    loadSettingsData() {
        this.isBusyMessage = 'Loading Email Server & Notifications ...';
        this.isBusy = true;
        this.isDirty = false;
        this.restService.post(Endpoint.realmManager_getMailServerConfig, { systemScope: true }).then(response => {
            if (response && response.mailServerConfig) {
                this.populateEmailSeverSettingsDataFromResponse(response.mailServerConfig);
                this.serverConfig = response.mailServerConfig;
            } else {
                this.isBusy = false;
            }
        }, error => {
            this.isBusy = false;
        });
    }

    populateEmailSeverSettingsDataFromResponse(response: Partial<EmailServer>): void {
        if (response) {
            this.settings = new EmailServer(response);
            this.viewModel = new EmailServerViewModel(this.settings);
        }

        this.restService.post(Endpoint.realmManager_listEmailIdsToNotify, { systemScope: true }).then(response => {
            if (response && response.emailIds) {
                this.listEmailToNotifyFromResponse(response);
            }
        }, error => {
            this.isBusy = false;
        });
    }

    listEmailToNotifyFromResponse(response: any): void {
        if (response) {
            if (response.emailIds) {
                this.notificationAddressList = response.emailIds.map(email => new DrNamedQuery({ name: email }));
            }

            const emptyNamedQuery = new DrNamedQuery({ name: '', query: '', description: null, annotationText: null });
            this.notificationAddressList.push(new DrNamedQuery(emptyNamedQuery));
        }
        this.isBusy = false;
        this.isDirty = false;
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.helpIndex = HelpIndex.sys_set_mailserver_notif_dlg;
        this.loadSettingsData();
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    onChange() {
        this.mailUserId.invalid = false;
        this.mailUserPassword.invalid = false;
        this.isDirty = this.viewModel.isDirty();
        this.viewModel.model.mailUserPassword = this.contentForm.value.mailUserPasswordInput ? this.contentForm.value.mailUserPasswordInput : this.viewModel.model.mailUserPassword;
        this.viewModel.model.isUsingSsl = this.viewModel.model.smtpHostPort === EmailServerViewModel.hostPortSslDefaultValue ? true : false;
    }

    saveChanges(): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            if (this.viewModel.model.smtpHostPort === '' || isNullOrUndefined(this.viewModel.model.smtpHostPort)) {
                resolve(false);
            } else {
                const requestInfo: any = {
                    mailUser: this.viewModel.model.mailUserId,
                    mailUserPasswrod: this.viewModel.model.mailUserPassword,
                    storeProtocol: this.viewModel.model.storeProtocol,
                    smtpHostId: this.viewModel.model.smtpHostId,
                    mailSmtpAuth: this.viewModel.model.mailSmtpAuth,
                    smtpHostPort: this.viewModel.model.smtpHostPort,
                    systemScope: true
                };

                const endpoint = isNullOrUndefined(this.serverConfig) ? Endpoint.realmManager_createMailServerConfig : Endpoint.realmManager_updateMailServerConfig;
                if (this.serverConfig) {
                    requestInfo.mailServerConfigHandle = this.serverConfig?.handle;
                }
                this.isBusyMessage = 'Saving Email Server Configuration ...';
                this.isBusy = true;

                this.restService.post(endpoint, requestInfo).then(response => {
                    this.resetAndLoad();
                    resolve(true);
                }, error => {
                    this.isBusy = false;
                    resolve(false);
                });

                const emails = DrNamedQuery.removeEmptyNames(this.notificationAddressList);
                const emailsToSend = emails.map(item => item.name.trim());

                this.restService.post(Endpoint.realmManager_setEmailNotificationCfg, { emailIds: emailsToSend, systemScope: true }).then(response => {
                    this.resetAndLoad();
                    resolve(true);
                }, error => {
                    this.isBusy = false;
                    resolve(false);
                });
            }
        });
    }

    resetAndLoad(): void {
        this.isBusy = false;
        this.isDirty = false;
        this.resetState();
        this.loadSettingsData();
    }

    isSMTPAuthenticationDisabled(): boolean {
        return !this.viewModel.model.smtpHostId || !this.viewModel.model.smtpHostPort;
    }

    resetState(): void {
        if (!isNullOrUndefined(this.queriesKeyValue)) {
            this.queriesKeyValue.resetState();
        }

        this.hostId.invalid = this.hostId.invalidIP = false;
        this.hostPort.invalid = this.hostPort.invalidPort = false;
        this.mailUserId.invalid = false;
        this.mailUserPassword.invalid = false;
    }

    discardChanges(): void {
        this.isDirty = false;
        this.loadSettingsData();
        this.resetState();
    }

    isSaveDisabled(): boolean {
        let isEnabled = false;
        this.validChanges = this.queriesKeyValue?.valid;
        if (!this.isBusy) {
            if (this.queriesKeyValue?.keyValueForm?.form.dirty && this.queriesKeyValue.valid) {
                isEnabled = true;
            }
            if ((this.viewModel.model.smtpHostId !== '' && !this.hostId.invalidIP) && (this.viewModel.model.smtpHostPort !== '' && !this.hostPort.invalidPort)) {
                isEnabled = true;
            }
            if (this.viewModel.model.mailSmtpAuth &&
                ((this.mailUserId.invalid || this.viewModel.model.mailUserId?.trim() === '') || (this.viewModel.model.mailUserPassword === '' || this.mailUserPassword.invalid))) {
                isEnabled = false;
            }
        }
        return !isEnabled;
    }

    isDiscardDisabled(): boolean {
        let isEnabled = false;
        this.validChanges = this.queriesKeyValue?.valid;
        if (!this.isBusy) {
            if (this.queriesKeyValue?.keyValueForm?.form.dirty || this.isDirty) {
                isEnabled = true;
            }
        }
        return !isEnabled;
    }

    updateConfig(value: any, type: string, eventType?: string): void {
        this.hostId.errorMessage = 'This field cannot be blank';
        this.hostId.invalid = this.hostId.invalidIP = false;
        this[type].invalid = value?.srcElement?.value === '' || value?.srcElement?.value === '0';
        if (type === 'hostPort') {
            this.hostPort.invalidPort = value?.srcElement?.value === '' || value?.srcElement?.value === '0';
            this.hostPort.errorMessage = value?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
        }
        this.isDirty = this.viewModel.isDirty();

        if (this.viewModel.model.mailSmtpAuth && (this.mailUserId.invalid || this.mailUserPassword.invalid) && (this.hostId.invalidIP || this.hostPort.invalidPort)) {
            this.mailUserId.invalid = false;
            this.mailUserPassword.invalid = false;
        }

        if ((type === 'hostId' || type === 'hostPort') && this.viewModel.model.mailSmtpAuth) {
            if (value?.srcElement?.value === '') {
                this.mailUserId.invalid = false;
                this.mailUserPassword.invalid = false;
            } else {
                if (this.viewModel.model.mailUserId === '') {
                    this.mailUserId.invalid = true;
                }
                if (this.viewModel.model.mailUserPassword === '') {
                    this.mailUserPassword.invalid = true;
                }
            }
        }
    }

    validateIP(event: any): boolean {
        if (this.isIP(event) || this.isHostname(event)) {
            this.hostId.invalid = this.hostId.invalidIP = false;
            return (true);
        }
        this.hostId.errorMessage = event?.srcElement?.value === '' ? 'This field cannot be blank' : 'Invalid format';
        this.hostId.invalid = this.hostId.invalidIP = true;
        return (false);
    }

    isIP(event): boolean {
        return (/^(([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])\.){3}([0-9]|[1-9][0-9]|1[0-9]{2}|2[0-4][0-9]|25[0-5])$/.test(event.srcElement.value))
    }
    isHostname(event): boolean {
        return (/^(([a-zA-Z]|[a-zA-Z][a-zA-Z0-9\-]*[a-zA-Z0-9])\.)*([A-Za-z]|[A-Za-z][A-Za-z0-9\-]*[A-Za-z0-9])$/.test(event.srcElement.value))
    }

    restrictNumeric = (evt: any): boolean => {
        const e = event || evt; // for trans-browser compatibility
        const charCode = e.which || e.keyCode;
        if (charCode == 46)
            return true
        if (charCode > 31 && (charCode < 48 || charCode > 57))
            return false;
        return true;
    }

    restrictPort = (event: any): boolean => {
        if (event) {
            return (event.charCode === 8 || event.charCode === 0) ? false : event.charCode >= 48 && event.charCode <= 57;
        }
        return false;
    }

    validateInputOnPaste(event: any, type: string): void {
        setTimeout(() => {
            if (this.viewModel.model[type].match(/^[0-9]+$/) === null) {
                this.viewModel.model[type] = this.viewModel.model[type].replace(/\D/g, '');
            }
            this.changeDetector.detectChanges();
        });
    }

    sendTestEmail(email: any): void {
        const defaultText = 'Test Email';
        const emailsToSend = email === 'all' ? DrNamedQuery.removeEmptyNames(this.notificationAddressList).map(item => item.name) : [email.name];
        const requestInfo: any = {
            message: defaultText,
            mailServerHandle: null,
            severity: NotificationSeverityCode.INFO,
            subject: defaultText + ' server configuration',
            summary: defaultText,
            toEmailIds: emailsToSend,
            systemScope: true
        };

        if (this.viewModel.model.mailSmtpAuth && this.viewModel.model.mailUserId) {
            requestInfo.from = this.viewModel.model.mailUserId?.trim() == "" ? null : this.viewModel.model.mailUserId;
        }
        this.restService.post(Endpoint.realmManager_sendEmail, requestInfo).then(() => {
            const message = `Test Emails have been sent to the following addresses:\n${emailsToSend.map((item) => `${item}`.trim()).join('\n')}`;
            this.commonService.openInformationToastNotification(message.replace(/\n/g, '<br/>'));
        });
    }

    isEmailSendTestDisabled(index: any): boolean {
        const errorsExist = this.queriesKeyValue?.popoverErrors.filter(pe => pe.error).length;
        this.isEmailAllDisabled = errorsExist > 0;

        if (this.queriesKeyValue) {
            return this.queriesKeyValue?.popoverErrors[index]?.error || this.queriesKeyValue.dataSource?.data[index]?.name === '';
        }
    }

    isDataSaved(): boolean {
        return this.viewModel.model.smtpHostId === this.viewModel.original.smtpHostId && this.viewModel.model.smtpHostPort === this.viewModel.original.smtpHostPort;
    }

    setSmtpHostPort($event): void {
        this.viewModel.model.smtpHostPort = $event ? EmailServerViewModel.hostPortSslDefaultValue : EmailServerViewModel.hostPortDefaultValue;
        this.isDirty = this.viewModel.isDirty();
        this.hostPort.invalid = this.hostPort.invalidPort = false;
    }
}
